/**
 * RecordRunLockBanner — pasek nad formularzem rekordu na czas zadania przycisku SQL (decyzja „A”).
 *
 * Na czas zadania (w kolejce lub w toku) rekordu nie da się zapisać ani usunąć — dla wszystkich
 * użytkowników; pasek mówi, które zadanie, od kiedy i czyje. Znika, gdy zadanie się kończy
 * (stan zadań rekordu pochodzi z `useRecordProcedureRuns`, który odpytuje serwer co ~2 s).
 *
 * Gdy odświeżenie stanu zadania się nie powiodło (`refreshFailed`; odpytywanie jest wtedy wstrzymane),
 * pasek dopisuje krótką informację — pokazany stan może być nieaktualny.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { runLockText } from '@/lib/record-procedures/run-texts';
import type { RecordProcedureRun } from '@/lib/record-procedures/runs-api';

export interface RecordRunLockBannerProps {
  run: RecordProcedureRun;
  /** Napis przycisku (albo nazwa procedury, gdy układ go nie zna) — „Trwa zadanie „<napis>”…”. */
  label: string;
  /** Ostatni odczyt stanu zadań się nie udał — pasek ostrzega, że stan może być nieaktualny. */
  refreshFailed?: boolean;
  testId?: string;
}

export function RecordRunLockBanner({
  run,
  label,
  refreshFailed = false,
  testId = 'record-run-lock-banner',
}: RecordRunLockBannerProps): ReactElement {
  const { t, i18n } = useTranslation();
  return (
    <Alert tone="info" icon={Lock} data-testid={testId}>
      {runLockText(run, label, t, i18n.language)}
      {refreshFailed ? (
        <span className="mt-1 block" data-testid={`${testId}-refresh-failed`}>
          {t('recordProcedureRuns.lockBanner.refreshFailed')}
        </span>
      ) : null}
    </Alert>
  );
}
